import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  calculateAmountsOfLineItems,
  calculateAmountsOfSingleLineItem,
  filterBudgetLineItems,
  sortBudgets,
} from '../../Pages/BudgetsPage/helpers/budgetHelper';
import { BUDGET_VIEW_OPTIONS, SLICE_STATUS } from '../../utils/constants';
import {
  BUDGET_TABLE_FILTERS,
  BudgetFilters,
  BudgetSettings,
  UpdateBudgetSettingsRequest,
  UpdateLineItemActualsRequest,
} from '../../utils/constants/budget';
import {
  BudgetModel,
  BudgetStatus,
  BudgetStatusChangeRequest,
  CreateBudgetRequest,
  CreateFileUploadRequest,
  CreateLineItemRequest,
  DuplicateBudgetRequest,
  LineItemWithAmounts,
  Option,
  Status,
  UpdateBudgetAmountRequest,
  UpdateFixedBrokenLineItemsRequest,
} from '../../utils/customTypes';
import { BudgetAndLineItemAmounts } from '../../utils/types/budget';
import { RootState } from '../store';
import BudgetListAPI from './budgetListAPI';

interface BudgetListState {
  budgetList: BudgetModel[];
  currentBudget?: BudgetModel;
  lineItemList?: LineItemWithAmounts[];
  laborOfCostLineItems: LineItemWithAmounts[];
  projectCostOfLaborAmounts: { projectId: string; actual: number }[];
  budgetViewType?: Option;
  filters: BudgetFilters;
  reportFilters: BudgetFilters;
  budgetAmount?: number[];
  projectAllBudgetInfo: BudgetAndLineItemAmounts[];
  showBrokenModal?: boolean;
  budgetSettings?: BudgetSettings;

  budgetStatus: Status;
  lineItemStatus: Status;
  lineItemFilesStatus: Status;
  lineItemNoteStatus: Status;
  activeBudgetLineItemStatus: Status;
  budgetSettingsStatus: Status;
}

/* ============================= INITIAL STATE ============================== */
const initialState: BudgetListState = {
  budgetList: [],
  lineItemList: [],
  laborOfCostLineItems: [],
  projectCostOfLaborAmounts: [],
  filters: {},
  reportFilters: {},
  budgetViewType: BUDGET_VIEW_OPTIONS[0],
  budgetAmount: [],
  projectAllBudgetInfo: [],
  showBrokenModal: false,
  budgetSettings: undefined,

  budgetStatus: SLICE_STATUS.IDLE,
  lineItemStatus: SLICE_STATUS.IDLE,
  lineItemNoteStatus: SLICE_STATUS.IDLE,
  lineItemFilesStatus: SLICE_STATUS.IDLE,
  activeBudgetLineItemStatus: SLICE_STATUS.IDLE,
  budgetSettingsStatus: SLICE_STATUS.IDLE,
};

const budgetListAPI = BudgetListAPI;

/* ============================== REDUX THUNK =============================== */

export const fetchBudgets = createAsyncThunk(
  'budgets/FETCH_BUDGETS',
  async () => {
    const response = await budgetListAPI.fetchBudgetList();
    return response;
  }
);

export const fetchLineItemsAmountsByBudgetId = createAsyncThunk(
  'budgets/FETCH_LINE_ITEMS_AMOUNTS',
  async (budgetId: string) => {
    const response = await budgetListAPI.fetchLineItemsAmountsByBudgetId(
      budgetId
    );
    return response;
  }
);

export const fetchLineItemsFilesByLineItemId = createAsyncThunk(
  'budgets/FETCH_LINE_ITEMS_FILES',
  async (lineItemId: string) => {
    const response = await budgetListAPI.fetchAttachmentsByLineItemId(
      lineItemId
    );
    return response;
  }
);

export const createNewBudget = createAsyncThunk(
  'budgets/CREATE_BUDGET',
  async (newBudgetData: CreateBudgetRequest, { getState }) => {
    const response = await budgetListAPI.createNewBudget({
      ...newBudgetData,
    });
    return response;
  }
);

export const duplicateBudget = createAsyncThunk(
  'budgets/DUPLICATE_BUDGET',
  async (newBudgetData: DuplicateBudgetRequest, { getState }) => {
    const response = await budgetListAPI.duplicateBudget({
      ...newBudgetData,
    });
    return response;
  }
);

export const renameBudget = createAsyncThunk(
  'budgets/RENAME_BUDGET',
  async ({ id, title }: { id: string; title: string }, { getState }) => {
    const response = await budgetListAPI.renameBudget(id, title);
    return response;
  }
);

export const changeBudgetStatus = createAsyncThunk(
  'budgets/CHANGE_STATUS',
  async (statusChangeRequest: BudgetStatusChangeRequest, { getState }) => {
    const response = await budgetListAPI.changeBudgetStatus({
      ...statusChangeRequest,
    });
    return response;
  }
);

export const updateLineItem = createAsyncThunk(
  'budgets/UPDATE_LINE_ITEM',
  async (lineItemRequest: CreateLineItemRequest, { getState }) => {
    const response = await budgetListAPI.updateLineItem({
      ...lineItemRequest,
    });
    return response;
  }
);

export const updateLineItemActual = createAsyncThunk(
  'budgets/UPDATE_LINE_ITEM_ACTUAL',
  async (lineItemRequest: UpdateLineItemActualsRequest) => {
    const response = await budgetListAPI.updateLineItemActual({
      ...lineItemRequest,
    });
    return response;
  }
);

export const updateLineItemActualForProject = createAsyncThunk(
  'budgets/UPDATE_LINE_ITEM_ACTUAL_FOR_PROJECT',
  async (lineItemRequest: UpdateLineItemActualsRequest) => {
    const response = await budgetListAPI.updateLineItemActualForProject({
      ...lineItemRequest,
    });
    return response;
  }
);

export const updateLineItemNote = createAsyncThunk(
  'budgets/UPDATE_LINE_ITEM_NOTE',
  async ({ id, note }: { id: string; note: string }, { getState }) => {
    const response = await budgetListAPI.updateLineItemNote(id, note);
    return response;
  }
);

export const updateLineItemNoteForProject = createAsyncThunk(
  'budgets/UPDATE_LINE_ITEM_NOTE_FOR_PROJECT',
  async ({ id, note }: { id: string; note: string }, { getState }) => {
    const response = await budgetListAPI.updateLineItemNote(id, note);
    return response;
  }
);

export const deleteBudget = createAsyncThunk(
  'budgets/DELETE',
  async (id: string, { getState }) => {
    const response = await budgetListAPI.deleteBudget(id);
    return response;
  }
);

export const deleteLineItem = createAsyncThunk(
  'budgets/DELETE_LINE_ITEM',
  async (id: string, { getState }) => {
    const response = await budgetListAPI.deleteLineItem(id);
    return response;
  }
);

export const saveBudgetFileUploadInfo = createAsyncThunk(
  'budgets/SAVE_UPLOADED_FILE_INFO',
  async (fileUploadRequest: CreateFileUploadRequest[], { getState }) => {
    const response = await budgetListAPI.saveBudgetFileUploadInfo(
      fileUploadRequest
    );
    return response;
  }
);

export const deleteBudgetFile = createAsyncThunk(
  'budgets/DELETE_UPLOADED_FILE',
  async (id: string, { getState }) => {
    const response = await budgetListAPI.deleteBudgetFile(id);
    return response;
  }
);

export const getProjectLineItemAmounts = createAsyncThunk(
  'budgets/GET_PROJECT_LINE_ITEM_AMOUNTS',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await budgetListAPI.getProjectLineItemInfo(projectId);
      return response;
    } catch (error) {
      return rejectWithValue(error as Error);
    }
  }
);

export const setBudgetFilters = createAction<BudgetFilters>(
  'budgets/SET_FILTERS'
);

export const fetchBudgetAmountsByBudgetId = createAsyncThunk(
  'budgets/FETCH_BUDGET_AMOUNTS',
  async (budgetId: string) => {
    const response = await budgetListAPI.fetchBudgetAmountsByBudgetId(budgetId);
    return response;
  }
);

export const updateBudgetAmount = createAsyncThunk(
  'budgets/UPDATE_BUDGET_AMOUNT',
  async (budgetAmountRequest: UpdateBudgetAmountRequest, { getState }) => {
    const response = await budgetListAPI.updateBudgetAmount({
      ...budgetAmountRequest,
    });
    return response;
  }
);

export const updateFixedBrokenLineItems = createAsyncThunk(
  'budgets/UPDATE_FIXED_LINE_ITEMS',
  async (
    updateFixedBrokenLineItemsRequest: UpdateFixedBrokenLineItemsRequest[],
    { getState }
  ) => {
    const response = await budgetListAPI.updateFixedBrokenLineItems(
      updateFixedBrokenLineItemsRequest
    );
    return response;
  }
);

export const fetchBudgetSettings = createAsyncThunk(
  'budgets/FETCH_BUDGET_SETTINGS',
  async () => {
    const response = await budgetListAPI.fetchBudgetSettings();
    return response;
  }
);

export const updateBudgetSettings = createAsyncThunk(
  'budgets/UPDATE_BUDGET_SETTINGS',
  async (updateBudgetSettingsRequest: UpdateBudgetSettingsRequest) => {
    const response = await budgetListAPI.updateBudgetSettings(
      updateBudgetSettingsRequest
    );
    return response;
  }
);

export const updateCostOfLabor = createAsyncThunk(
  'budgets/UPDATE_COST_OF_LABOR',
  async (lineItemRequest: CreateLineItemRequest, { getState }) => {
    const response = await budgetListAPI.updateCostOfLabor({
      ...lineItemRequest,
    });
    return response;
  }
);

export const setBudgetReportFilters = createAction<BudgetFilters>(
  'budgets/SET_REPORT_FILTERS'
);

export const fetchCostOfLaborActuals = createAsyncThunk(
  'budgets/FETCH_COST_OF_LABORS_ACTUALS',
  async (budgetId: string) => {
    const response = await budgetListAPI.fetchCostOfLaborActuals(budgetId);
    return response;
  }
);

/* ================================= REDUCER ================================ */
const budgetListSlice = createSlice({
  name: 'budgetList',
  initialState,
  reducers: {
    setShowBrokenModal(state, action) {
      state.showBrokenModal = action.payload;
    },

    setSelectedBudget(state, action) {
      const budget = state.budgetList.find(
        (budget) => budget.id === action.payload
      );
      if (budget) {
        state.currentBudget = budget;
        state.budgetViewType = BUDGET_VIEW_OPTIONS[0];
      }
    },

    setBudgetViewType(state, action) {
      const viewOption = BUDGET_VIEW_OPTIONS.find(
        (option) => option.value === action.payload.value
      );
      if (viewOption) {
        state.budgetViewType = viewOption;
      }
    },

    addNewLineItem: (
      state,
      action: {
        type: string;
        payload: {
          id?: string;
          learningTeamId?: string;
          programId?: string;
          vendorId?: string;
          projectId?: string;
        };
      }
    ) => {
      const uuid = uuidv4();
      const newLineItem: LineItemWithAmounts = {
        id: action.payload.id || uuid,
        description: 'New Line Item',
        organizationId: state.currentBudget!.organizationId,
        budgetId: state.currentBudget!.id,
        amounts: [],
        rawAmounts: Array(12).fill(0),
        rawActuals: Array(12).fill(0),
        currency: state.currentBudget!.currency,
        showInput: true,
        learningTeamId: action.payload.learningTeamId,
        programId: action.payload.programId,
        vendorId: action.payload.vendorId,
        projectId: action.payload.projectId,
      };
      state.lineItemList?.push(newLineItem);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBudgets.pending, (state, action) => {
        state.budgetList = [];
        state.currentBudget = undefined;
        state.budgetStatus = SLICE_STATUS.LOADING;
        state.lineItemStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchBudgets.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
        state.lineItemStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        state.lineItemStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          let budgetList = action.payload.data;
          budgetList = sortBudgets(budgetList);
          state.budgetList = budgetList;

          if (!state.currentBudget && budgetList.length) {
            const latestActiveBudget = budgetList
              .filter(
                (budget: BudgetModel) => budget.status === BudgetStatus.ACTIVE
              )
              .sort(
                (a: BudgetModel, b: BudgetModel) =>
                  new Date(b.fiscalPeriodStart).getTime() -
                  new Date(a.fiscalPeriodStart).getTime()
              );
            if (latestActiveBudget.length) {
              state.currentBudget = latestActiveBudget[0];
            } else {
              state.currentBudget = action.payload.data[0];
            }
          }
        }
      });

    builder
      .addCase(createNewBudget.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(createNewBudget.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(createNewBudget.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          let newBudgetList = [...state.budgetList, action.payload.data];
          newBudgetList = sortBudgets(newBudgetList);
          state.budgetList = newBudgetList;

          if (!state.currentBudget) {
            state.currentBudget = action.payload.data;
          }
        }
      });

    builder
      .addCase(duplicateBudget.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(duplicateBudget.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(duplicateBudget.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload', null)) {
          let newBudgetList = [...state.budgetList, action.payload];
          newBudgetList = sortBudgets(newBudgetList);
          state.budgetList = newBudgetList;
        }
      });

    builder.addCase(renameBudget.fulfilled, (state, action) => {
      state.budgetStatus = SLICE_STATUS.IDLE;
      if (get(action, 'payload', null)) {
        for (let item of state.budgetList) {
          if (item.id === action.payload.id) {
            item.title = action.payload.title;
            break;
          }
        }
        if (
          state.currentBudget &&
          state.currentBudget.id === action.payload.id
        ) {
          state.currentBudget.title = action.payload.title;
        }
      }
    });

    builder
      .addCase(changeBudgetStatus.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(changeBudgetStatus.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(changeBudgetStatus.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload', null)) {
          const updatedBudgets = state.budgetList.map((budget) => {
            const updatedBudget = action.payload.find(
              (b: BudgetModel) => b.id === budget.id
            );
            return updatedBudget
              ? { ...budget, status: updatedBudget.status }
              : budget;
          });
          state.budgetList = sortBudgets(updatedBudgets);

          if (state.currentBudget) {
            state.currentBudget = state.budgetList.find(
              (item) => item.id === state.currentBudget?.id
            );
          }
        }
      });

    builder
      .addCase(deleteBudget.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(deleteBudget.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(deleteBudget.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          state.budgetList = state.budgetList.filter(
            (item) => item.id !== action.payload.data.id
          );

          if (
            state.currentBudget?.id === action.payload.data.id &&
            state.budgetList.length
          ) {
            state.currentBudget = state.budgetList[0];
          } else if (state.currentBudget?.id === action.payload.data.id) {
            state.currentBudget = undefined;
            state.lineItemList = [];
            state.budgetAmount = [];
          }
        }
      });

    builder
      .addCase(fetchLineItemsAmountsByBudgetId.pending, (state) => {
        state.lineItemList = [];
        state.lineItemStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchLineItemsAmountsByBudgetId.rejected, (state) => {
        state.lineItemStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchLineItemsAmountsByBudgetId.fulfilled, (state, action) => {
        state.lineItemStatus = SLICE_STATUS.SUCCESS;
        const data = get(action, 'payload.data', null);
        if (data) {
          // Do not update line item amounts if the current budget changed in the meantime.
          if (state.currentBudget?.id === action.meta.arg) {
            const calculatedLineItems = calculateAmountsOfLineItems(
              data,
              state.currentBudget
            );
            state.laborOfCostLineItems = calculatedLineItems.filter(
              (item) => item.description === 'Cost of Labor'
            );
            state.lineItemList = calculatedLineItems.filter(
              (item) => item.description !== 'Cost of Labor'
            );
          }
        }
      });

    builder
      .addCase(updateLineItemActual.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateLineItemActual.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateLineItemActual.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          const calculatedLineItems = calculateAmountsOfLineItems(
            action.payload.data,
            state.currentBudget!
          );
          state.lineItemList = calculatedLineItems.filter(
            (item) => item.description !== 'Cost of Labor'
          );
        }
      });

    builder
      .addCase(fetchLineItemsFilesByLineItemId.pending, (state) => {
        state.lineItemFilesStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchLineItemsFilesByLineItemId.rejected, (state) => {
        state.lineItemFilesStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchLineItemsFilesByLineItemId.fulfilled, (state) => {
        state.lineItemFilesStatus = SLICE_STATUS.IDLE;
      });

    builder
      .addCase(saveBudgetFileUploadInfo.pending, (state) => {
        state.lineItemFilesStatus = SLICE_STATUS.LOADING;
      })
      .addCase(saveBudgetFileUploadInfo.rejected, (state) => {
        state.lineItemFilesStatus = SLICE_STATUS.FAILED;
      })
      .addCase(saveBudgetFileUploadInfo.fulfilled, (state, action) => {
        const uploadedFiles = action.payload.data;

        uploadedFiles.forEach((uploadedFile: { lineItemId: string }) => {
          const matchingLineItem = state.lineItemList?.find(
            (item) => item.id === uploadedFile.lineItemId
          );

          if (matchingLineItem) {
            matchingLineItem.hasFile = true;
          }
        });
        state.lineItemFilesStatus = SLICE_STATUS.IDLE;
      });

    builder
      .addCase(deleteBudgetFile.pending, (state) => {
        state.lineItemFilesStatus = SLICE_STATUS.LOADING;
      })
      .addCase(deleteBudgetFile.rejected, (state) => {
        state.lineItemFilesStatus = SLICE_STATUS.FAILED;
      })
      .addCase(deleteBudgetFile.fulfilled, (state, action) => {
        const lineItemFile = action.payload.data;
        const matchingLineItem = state.lineItemList?.find(
          (item) => item.id === lineItemFile.lineItemId
        );
        if (matchingLineItem) {
          matchingLineItem.hasFile = action.payload.data.hasMoreFile;
          // matchingLineItem.hasMoreFile = action.payload.data.hasMoreFile ?? false;
        }
        state.lineItemFilesStatus = SLICE_STATUS.IDLE;
      });

    builder
      .addCase(updateLineItemNote.pending, (state) => {
        state.lineItemNoteStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateLineItemNote.rejected, (state) => {
        state.lineItemNoteStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateLineItemNote.fulfilled, (state, action) => {
        state.lineItemNoteStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data')) {
          if (action.payload.data.description === 'Cost of Labor') {
            state.laborOfCostLineItems = state.laborOfCostLineItems!.map(
              (item) =>
                item.id === action.payload.data.id
                  ? { ...item, note: action.payload.data.note }
                  : item
            );
          } else {
            state.lineItemList = state.lineItemList!.map((item) =>
              item.id === action.payload.data.id
                ? { ...item, note: action.payload.data.note }
                : item
            );
          }
        }
      });

    builder
      .addCase(updateLineItemNoteForProject.pending, (state) => {
        state.lineItemNoteStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateLineItemNoteForProject.rejected, (state) => {
        state.lineItemNoteStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateLineItemNoteForProject.fulfilled, (state, action) => {
        state.lineItemNoteStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data')) {
          state.projectAllBudgetInfo = state.projectAllBudgetInfo.map(
            (budgetItem) => {
              budgetItem.lineItemAmounts = budgetItem.lineItemAmounts.map(
                (item) => {
                  if (item.id === action.payload.data.id) {
                    return {
                      ...item,
                      note: action.payload.data.note,
                    };
                  } else {
                    return item;
                  }
                }
              );
              return budgetItem;
            }
          );
        }
      });

    builder
      .addCase(deleteLineItem.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(deleteLineItem.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(deleteLineItem.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          state.lineItemList = state.lineItemList!.filter(
            (item) => item.id !== action.payload.data.id
          );
        }
      });

    builder.addCase(updateLineItem.fulfilled, (state, action) => {
      if (get(action, 'payload.data', null)) {
        let lineItemData: LineItemWithAmounts =
          calculateAmountsOfSingleLineItem(
            action.payload.data,
            state.currentBudget!
          );
        state.lineItemList = state.lineItemList?.map((item) =>
          item.id === lineItemData.id
            ? {
                ...item,
                ...lineItemData,
                projectId: lineItemData.projectId,
                programId: lineItemData.programId,
                learningTeamId: lineItemData.learningTeamId,
                vendorId: lineItemData.vendorId,
                budgetSource: lineItemData.budgetSource,
                showInput: false,
              } // Merge existing
            : item
        );
      }
    });

    builder
      .addCase(fetchBudgetAmountsByBudgetId.pending, (state) => {
        state.budgetAmount = [];
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchBudgetAmountsByBudgetId.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchBudgetAmountsByBudgetId.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          // Do not update amounts if the current budget changed in the meantime.
          if (state.currentBudget?.id === action.meta.arg) {
            state.budgetAmount = action.payload.data.rawAmounts;
          }
        }
      });

    builder
      .addCase(updateBudgetAmount.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateBudgetAmount.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateBudgetAmount.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          state.budgetAmount = action.payload.data.rawAmounts;
        }
      });

    builder
      .addCase(updateFixedBrokenLineItems.pending, (state, action) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateFixedBrokenLineItems.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateFixedBrokenLineItems.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          const lineItemList: {
            id: string;
            programId: string;
            learningTeamId: string;
            projectId: string;
          }[] = action.payload.data;

          const newLineItemsMap = new Map(
            lineItemList.map((item) => [item.id, item]) // Create a map with `id` as the key
          );

          state.lineItemList = state.lineItemList?.map((item) => {
            if (newLineItemsMap.has(item.id)) {
              const lineItem = newLineItemsMap.get(item.id);
              item.programId = lineItem?.programId;
              item.learningTeamId = lineItem?.learningTeamId;
              item.projectId = lineItem?.projectId;
            }
            return item;
          });
        }
      });

    builder.addCase(setBudgetFilters, (state, action) => {
      state.filters = action.payload;
    });

    builder.addCase(setBudgetReportFilters, (state, action) => {
      state.reportFilters = action.payload;
    });

    builder
      .addCase(getProjectLineItemAmounts.pending, (state) => {
        state.activeBudgetLineItemStatus = SLICE_STATUS.LOADING;
      })
      .addCase(getProjectLineItemAmounts.rejected, (state, action) => {
        if (get(action, 'payload', null)) {
          const errorCode = get(action, 'payload');
          if (errorCode && errorCode === 404) {
            state.projectAllBudgetInfo = [];
          }
        }
        state.activeBudgetLineItemStatus = SLICE_STATUS.FAILED;
      })
      .addCase(getProjectLineItemAmounts.fulfilled, (state, action) => {
        state.activeBudgetLineItemStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          if (
            action.payload.data.budgetsWithLineItemAmounts &&
            action.payload.data.budgetsWithLineItemAmounts.length
          ) {
            let newProjectAllBudgetInfo: BudgetAndLineItemAmounts[] = [];
            action.payload.data.budgetsWithLineItemAmounts.forEach(
              (budgetInfoWithLineItem: BudgetAndLineItemAmounts) => {
                newProjectAllBudgetInfo.push({
                  budget: budgetInfoWithLineItem.budget,
                  lineItemAmounts: calculateAmountsOfLineItems(
                    budgetInfoWithLineItem.lineItemAmounts,
                    budgetInfoWithLineItem.budget
                  ),
                });
              }
            );
            state.projectAllBudgetInfo = newProjectAllBudgetInfo;
          }
        }
      });

    builder
      .addCase(updateLineItemActualForProject.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateLineItemActualForProject.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateLineItemActualForProject.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          // updating the line item amounts for the active budget
          state.projectAllBudgetInfo = state.projectAllBudgetInfo.map(
            (budgetInfo) => {
              if (budgetInfo.budget.id === action.payload.data.budgetId) {
                return {
                  ...budgetInfo,
                  lineItemAmounts: calculateAmountsOfLineItems(
                    budgetInfo.lineItemAmounts.map((item) =>
                      item.id === action.payload.data.id
                        ? action.payload.data
                        : item
                    ),
                    budgetInfo.budget
                  ),
                };
              }
              return budgetInfo;
            }
          );
        }
      });

    builder
      .addCase(fetchBudgetSettings.pending, (state) => {
        state.budgetSettingsStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchBudgetSettings.rejected, (state) => {
        state.budgetSettingsStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchBudgetSettings.fulfilled, (state, action) => {
        state.budgetSettingsStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          state.budgetSettings = action.payload.data;
        }
      });

    builder
      .addCase(updateBudgetSettings.pending, (state) => {
        state.budgetSettingsStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateBudgetSettings.rejected, (state) => {
        state.budgetSettingsStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateBudgetSettings.fulfilled, (state, action) => {
        state.budgetSettingsStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          state.budgetSettings = action.payload.data;
        }
      });

    builder
      .addCase(fetchCostOfLaborActuals.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchCostOfLaborActuals.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchCostOfLaborActuals.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          // Do not update amounts if the current budget changed in the meantime.
          if (state.currentBudget?.id === action.meta.arg) {
            state.projectCostOfLaborAmounts = action.payload.data;
          }
        }
      });

    builder
      .addCase(updateCostOfLabor.pending, (state) => {
        state.budgetStatus = SLICE_STATUS.LOADING;
      })
      .addCase(updateCostOfLabor.rejected, (state) => {
        state.budgetStatus = SLICE_STATUS.FAILED;
      })
      .addCase(updateCostOfLabor.fulfilled, (state, action) => {
        state.budgetStatus = SLICE_STATUS.IDLE;
        if (get(action, 'payload.data', null)) {
          const newLaborOfCostLineItem = action.payload.data;

          if (
            !state.laborOfCostLineItems?.some(
              (item) => item.projectId === newLaborOfCostLineItem.projectId
            )
          ) {
            state.laborOfCostLineItems = [
              ...(state.laborOfCostLineItems || []),
              newLaborOfCostLineItem,
            ];
          } else {
            state.laborOfCostLineItems = state.laborOfCostLineItems.map(
              (item) =>
                item.projectId === newLaborOfCostLineItem.projectId
                  ? newLaborOfCostLineItem
                  : item
            );
          }
        }
      });
  },
});

export const selectBudgetSettings = (state: RootState) => {
  return state.budgetList.budgetSettings;
};

export const selectListBudget = (state: RootState) => {
  return state.budgetList.budgetList || [];
};

export const selectProjectCostOfLaborAmounts = (state: RootState) => {
  return state.budgetList.projectCostOfLaborAmounts || [];
};

export const selectLaborOfCostLineItems = (state: RootState) => {
  return state.budgetList.laborOfCostLineItems || [];
};

export const selectCostOfLaborFilter = (state: RootState) => {
  return !!state.budgetList.filters[BUDGET_TABLE_FILTERS.ADDITIONAL_FLAGS];
};

export const selectBudgetAmounts = (state: RootState) => {
  return state.budgetList.budgetAmount || [];
};

export const selectLineItems = (state: RootState) => {
  return state.budgetList.lineItemList || [];
};

export const lineItemFilters = (state: RootState) => state.budgetList.filters;

export const selectFilteredLineItems = createSelector(
  [selectLineItems, lineItemFilters],
  (lineItems, filters) => {
    return filterBudgetLineItems(lineItems, filters);
  }
);

export const getSelectedBudget = (state: RootState) =>
  state.budgetList.currentBudget;

export const getBudgetViewType = (state: RootState) =>
  state.budgetList.budgetViewType;

export const getBudgetListStatus = (state: RootState) =>
  state.budgetList.budgetStatus;

export const getLineItemStatus = (state: RootState) =>
  state.budgetList.lineItemStatus;

export const getProjectLineItemStatus = (state: RootState) =>
  state.budgetList.activeBudgetLineItemStatus;

export const getProjectAllBudgetInfo = (state: RootState) =>
  state.budgetList.projectAllBudgetInfo;

export const getShowBrokenModal = (state: RootState) =>
  state.budgetList.showBrokenModal || false;
/* =============================== SELECTORS ================================ */

export const {
  setSelectedBudget,
  addNewLineItem,
  setBudgetViewType,
  setShowBrokenModal,
} = budgetListSlice.actions;

export default budgetListSlice.reducer;
